import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/clientUtils";
import { SidebarTrigger } from "./sidebar";

const headingVariants = cva("", {
  defaultVariants: {
    size: "default",
    variant: "default",
  },
  variants: {
    size: {
      default: "text-2xl",
      internal: "text-xl",
      lg: "text-3xl",
      sm: "text-xl",
    },
    variant: {
      card: "font-bold text-gray-900 leading-9 tracking-tight",
      default: "font-bold text-gray-900 leading-9 tracking-tight",
      internal: "font-semibold text-gray-900 leading-7 tracking-tight",
      section: "font-semibold",
      sheet: "font-semibold text-gray-900 leading-7 tracking-tight",
    },
  },
});

const textVariants = cva("", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      card: "text-gray-600 text-xs md:text-sm",
      default: "text-base text-gray-600 leading-7",
      labelNonofficial: "font-semibold text-sm leading-none",
      large: "text-gray-600 text-lg leading-7",
      link: "text-primary underline underline-offset-4 hover:text-primary/90",
      muted: "text-gray-600 text-sm",
      small: "text-gray-500 text-sm leading-6",
      tabs: "font-semibold text-xs tracking-tight",
    },
  },
});

type TextProps = {
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
} & VariantProps<typeof textVariants> &
  Omit<React.HTMLAttributes<HTMLElement>, "className">;

interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  wrapperClassName?: string;
}

export function Heading({
  className,
  variant,
  size,
  as = "h2",
  ...props
}: HeadingProps) {
  const Component = as;
  return (
    <Component
      className={cn(headingVariants({ size, variant }), className)}
      {...props}
    />
  );
}

export function PageHeading({
  className,
  wrapperClassName,
  variant,
  size,
  ...props
}: HeadingProps) {
  return (
    <div
      className={cn(
        "my-0 flex flex-row items-center space-x-2 text-nowrap",
        wrapperClassName,
      )}
    >
      <SidebarTrigger className="block md:hidden" />
      <h1
        className={cn(headingVariants({ size, variant }), className)}
        {...props}
      />
    </div>
  );
}

export function Text({ className, variant, as = "p", ...props }: TextProps) {
  const Component = as;
  return React.createElement(Component, {
    className: cn(textVariants({ variant }), className),
    ...props,
  });
}
